const triggers = {};
/**
 * register a trigger
 * @param  {string} triggerName - name of the trigger
 */
export function registerTrigger(triggerName) {
    if (!triggers[triggerName]) {
        triggers[triggerName] = [];
    }
}
/**
 * register multiple triggers at once
 * @param  {Array} triggerNames - array of trigger names
 */
export function registerTriggers(triggerNames) {
    if (triggerNames && triggerNames.length > 0 && triggerNames.forEach) {
        triggerNames.forEach(registerTrigger);
    }
}

/**
 * reset all subscriptions to a single trigger
 * @param {string} triggerName - name of the trigger
 */
export function resetTrigger(triggerName) {
    triggers[triggerName] = [];
}

/**
 * reset all subscriptions to every trigger in the array
 * @param {Array} triggerNames - array of trigger names
 */
export function resetTriggers(triggerNames) {
    if (triggerNames && triggerNames.length > 0 && triggerNames.forEach) {
        triggerNames.forEach(resetTrigger);
    }
}

/**
 * subscribe to the notification by triggername
 * @param {string} triggerName - name of the trigger to subscribe to
 * @param {function} fn - function to execute on trigger
 */
export function subscribeNotify(triggerName, fn) {
    if (!triggers[triggerName]) {
        triggers[triggerName] = [];
    }
    if (fn && typeof fn === 'function') {
        triggers[triggerName].push(fn)
    }
}

/**
 * subscribe to the notification by triggername and more specific bij naming the function
 * @param {string} triggerName - name of the trigger to subscribe to
 * @param {function} fn - function to execute on trigger
 * @param {string} name - name of the function to execute
 */
export function subscribeNamedNotify(triggerName, {fn, name}) {
    if (!triggers[triggerName]) {
        triggers[triggerName] = [];
    }
    if (fn && typeof fn === 'function' && name && typeof name === 'string') {
        triggers[triggerName].push({name, fn});
    }
}

/**
 * subscribe to the notification by triggername and more specific bij unique naming on the function
 * so there can only be one function with a certain name subscribed to a trigger enabling returning a value
 * if there is already a function with a certain name subscribed to a trigger it will be replaced
 * @param {string} triggerName - name of the trigger to subscribe to
 * @param {function} fn - function to execute on trigger
 * @param {string} name - unique name of the function to execute
 */
export function subscribeUniqueNamedNotify(triggerName, {fn, name}) {
    if (!triggers[triggerName]) {
        triggers[triggerName] = [];
    }
    if (fn && typeof fn === 'function' && name && typeof name === 'string') {
        let tr = triggers[triggerName].find(trigger => trigger.name === name);
        if (tr) {
            tr.fn = fn;
        } else {
            triggers[triggerName].push({name, fn});
        }
    }
}

/**
 * unsubscribe from the notification by triggername
 * @param {string} triggerName - name of the trigger to unsubscribe from
 * @param {function} fn - function to unsubscribe
 */
export function unsubscribeNotify(triggerName, fn) {
    if (!triggers[triggerName]) {
        return;
    }
    if (fn && typeof fn === 'function') {
        triggers[triggerName] = triggers[triggerName].filter(fun => fun !== fn);
    }
}

/**
 * unsubscribe from the notification by triggername and more specific bij the function name
 * @param {string} triggerName - name of the trigger to unsubscribe from
 * @param {function} fn - function to unsubscribe
 * @param {string} name - name of the function to unsubscribe
 */
export function unsubscribeNamedNotify(triggerName, {fn, name}) {
    if (!triggers[triggerName]) {
        return;
    }
    if (fn && typeof fn === 'function' && name && typeof name === 'string') {
        triggers[triggerName] = triggers[triggerName].filter(obj => obj.name !== name && obj.fn !== fn);
    }
}

/**
 * unsubscribe from the notification by triggername and more specific bij the function name
 * @param {string} triggerName - name of the trigger to unsubscribe from
 * @param {function} fn - function to unsubscribe
 * @param {string} name - name of the function to unsubscribe
 */
export function unsubscribeUniqueNamedNotify(triggerName, {fn, name}) {
    if (!triggers[triggerName]) {
        return;
    }
    if (fn && typeof fn === 'function' && name && typeof name === 'string') {
        triggers[triggerName] = triggers[triggerName].filter(obj => obj.name !== name);
    }
}

/************************
 * All trigger functions are defined as async functions
 * Async functions always return Promises
 * When more than one function has subscribed to the notification they are executed in parallel
 * by means of Promise.all
 ************************/

/**
 * trigger the subscribed functions and possibly pass them some arguments
 * @param {string} triggerName - name of the trigger
 * @param {Array} args - argument collection to pass to the subscribed functions
 * @returns an Array of promises that will resolve with true or reject with an error
 */
export async function triggerNotify(triggerName, ...args) {
    if (!triggers[triggerName]) {
        return;
    }

    const promiseArray = triggers[triggerName].map(fun => fun(...args));
    return Promise.all(promiseArray);
}

/**
 * trigger the subscribed functions that have the same name and possibly pass them some arguments
 * @param {string} triggerName - name of the trigger
 * @param {object} args - arguments to pass to the subscribed functions
 * @param {string} name - name of the function
 * @returns an Array of promises that will resolve with true or reject with an error
 */
export async function triggerNamedNotify(triggerName, {name, args = {}}) {
    if (!triggers[triggerName] || !(triggers[triggerName].length > 0 && name && typeof name === 'string')) {
        return;
    }
    let namedFuns = triggers[triggerName].filter(obj => obj.name === name && typeof obj.fn === 'function') || [];
    const promiseArray = namedFuns.map(namedFun => namedFun.fn(args));
    return Promise.all(promiseArray);
}

/**
 * trigger the unique subscribed function with the specified name and possibly pass it some arguments
 * @param {string} triggerName - name of the trigger
 * @param {object} args - arguments to pass to the subscribed functions
 * @param {string} name - name of the function
 * @returns a promise that will resolve with the result of the function or reject with an error
 */
export async function triggerUniqueNamedNotify(triggerName, {name, args = {}}) {
    if (!triggers[triggerName]) {
        throw new Error(`trigger with name: ${triggerName} does not exist!`);
    }
    if (triggers[triggerName].length > 0 && name && typeof name === 'string' ) {
        let namedFun = triggers[triggerName].find(obj => obj.name === name && typeof obj.fn === 'function');
        if (namedFun) {
            return namedFun.fn(args);
        }
    }
}